/**
 * CatalogFilters — gestisce navigazione filtri catalogo corsi
 *
 * Raccoglie lo stato corrente dalla query string e dal path (rotte pulite),
 * aggiorna l'URL al cambio di qualsiasi filtro e fa reload SSR della pagina.
 *
 * Rotte pulite supportate:
 *   /corsi-ecm/fad/                         → type=1
 *   /corsi-ecm/residenziali/                → type=2
 *   /corsi-ecm/fad/{slug}/                  → type=1 + occupation
 *   /corsi-ecm/residenziali/{slug}/         → type=2 + occupation
 *   /corsi-ecm/{slug}/                      → occupation
 *   /corsi-ecm/{slug}/{id}p/                → occupation (legacy)
 *   /corsi-ecm/promo/global-card/           → product_type=global_card
 *   /corsi-ecm/promo/bundle-corsi/          → product_type=package_bundle
 *   /corsi-ecm/promo/bundle-crediti/        → product_type=package_credit
 *
 * Uso:
 *   <div x-data="catalogFilters">
 *     <!-- sidebar con radio/checkbox che hanno @change="applyFilter(key, value)" -->
 *     <!-- chip con @click="removeFilter(key, value)" -->
 *   </div>
 */

function slugify(text) {
    return text
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

/** Mappa tipo → segmento URL e viceversa */
const TYPE_SEGMENTS = { '1': 'fad', '2': 'residenziali' };
const SEGMENT_TYPES = { 'fad': '1', 'residenziali': '2' };

export function registerCatalogFilters(Alpine) {
    Alpine.data('catalogFilters', () => ({
        mobileOpen: false,

        /** Segmenti riservati che NON sono slug professione */
        reservedSlugs: ['fad', 'residenziali', 'promo'],

        /**
         * Legge i parametri correnti dalla URL (query string + path impliciti).
         */
        getParams() {
            const params = new URLSearchParams(window.location.search);
            const path = window.location.pathname;

            // Rotte statiche (promo)
            const promoMap = [
                { pattern: /\/corsi-ecm\/promo\/global-card\/?$/,     key: 'product_type', value: 'global_card' },
                { pattern: /\/corsi-ecm\/promo\/bundle-corsi\/?$/,    key: 'product_type', value: 'package_bundle' },
                { pattern: /\/corsi-ecm\/promo\/bundle-crediti\/?$/,  key: 'product_type', value: 'package_credit' },
            ];
            for (const route of promoMap) {
                if (route.pattern.test(path)) {
                    params.set(route.key, route.value);
                    return params;
                }
            }

            // /corsi-ecm/(fad|residenziali)/{slug}/ — tipo + professione
            const comboMatch = path.match(/\/corsi-ecm\/(fad|residenziali)\/([^/]+)\/?$/);
            if (comboMatch) {
                params.set('type', SEGMENT_TYPES[comboMatch[1]]);
                params.set('_occupation_slug', comboMatch[2]);
                return params;
            }

            // /corsi-ecm/(fad|residenziali)/ — solo tipo
            const typeMatch = path.match(/\/corsi-ecm\/(fad|residenziali)\/?$/);
            if (typeMatch) {
                params.set('type', SEGMENT_TYPES[typeMatch[1]]);
                return params;
            }

            // Legacy: /corsi-ecm/{nome}/{id}p/ — professione con ID
            const legacyMatch = path.match(/\/corsi-ecm\/[^/]+\/(\d+)p\/?$/);
            if (legacyMatch) {
                params.set('professione', legacyMatch[1]);
                return params;
            }

            // /corsi-ecm/{slug}/ — solo professione (slug non riservato)
            const slugMatch = path.match(/\/corsi-ecm\/([^/]+)\/?$/);
            if (slugMatch && !this.reservedSlugs.includes(slugMatch[1])) {
                params.set('_occupation_slug', slugMatch[1]);
                return params;
            }

            return params;
        },

        /**
         * Naviga alla URL con i parametri aggiornati.
         * Costruisce il path più pulito possibile.
         */
        navigate(params) {
            params.delete('page');

            const occSlug = params.get('_occupation_slug');
            const typeVal = params.get('type') || params.get('tipo');
            const typeSeg = typeVal ? TYPE_SEGMENTS[typeVal] : null;

            // Combinazione tipo + professione → /corsi-ecm/fad/{slug}/
            if (occSlug && typeSeg) {
                params.delete('_occupation_slug');
                params.delete('professione');
                params.delete('occupation');
                params.delete('type');
                params.delete('tipo');
                const basePath = '/corsi-ecm/' + typeSeg + '/' + occSlug + '/';
                const qs = params.toString();
                window.location.href = basePath + (qs ? '?' + qs : '');
                return;
            }

            // Solo professione → /corsi-ecm/{slug}/
            if (occSlug) {
                params.delete('_occupation_slug');
                params.delete('professione');
                params.delete('occupation');
                const basePath = '/corsi-ecm/' + occSlug + '/';
                const qs = params.toString();
                window.location.href = basePath + (qs ? '?' + qs : '');
                return;
            }

            // Rotte pulite per tipo e product_type
            let basePath = '/corsi-ecm/';
            const cleanPaths = {
                type:         { '1': '/corsi-ecm/fad/',  '2': '/corsi-ecm/residenziali/' },
                product_type: {
                    'global_card':    '/corsi-ecm/promo/global-card/',
                    'package_bundle': '/corsi-ecm/promo/bundle-corsi/',
                    'package_credit': '/corsi-ecm/promo/bundle-crediti/',
                },
            };

            for (const [key, paths] of Object.entries(cleanPaths)) {
                const val = params.get(key);
                if (val && paths[val]) {
                    basePath = paths[val];
                    params.delete(key);
                    break;
                }
            }

            const qs = params.toString();
            window.location.href = basePath + (qs ? '?' + qs : '');
        },

        /**
         * Applica un filtro: setta il parametro e naviga.
         * type e product_type sono dominanti: resettano tutto tranne la professione.
         */
        applyFilter(key, value, meta) {
            // Ricerca testuale vuota: equivale a togliere il filtro (niente "?ts=" in URL,
            // che farebbe perdere la pagina canonica).
            if (key === 'ts' || key === 'search') {
                value = String(value ?? '').trim();
                if (!value) {
                    this.removeFilter('search');
                    return;
                }
            }

            if (key === 'product_type') {
                const params = new URLSearchParams();
                params.set(key, value);
                this.navigate(params);
                return;
            }

            if (key === 'type') {
                // Tipo preserva la professione attiva (per URL combinato)
                const params = new URLSearchParams();
                params.set(key, value);
                // Recupera slug professione dal path corrente
                const path = window.location.pathname;
                const comboMatch = path.match(/\/corsi-ecm\/(?:fad|residenziali)\/([^/]+)\/?$/);
                const slugMatch = path.match(/\/corsi-ecm\/([^/]+)\/?$/);
                if (comboMatch) {
                    params.set('_occupation_slug', comboMatch[1]);
                } else if (slugMatch && !this.reservedSlugs.includes(slugMatch[1])) {
                    params.set('_occupation_slug', slugMatch[1]);
                }
                this.navigate(params);
                return;
            }

            const params = this.getParams();

            // Professione: naviga con slug pulito
            if (key === 'professione' && meta) {
                params.delete('professione');
                params.delete('occupation');
                params.set('_occupation_slug', slugify(meta));
                this.navigate(params);
                return;
            }

            params.set(key, value);
            this.navigate(params);
        },

        /**
         * Rimuove un filtro e naviga.
         */
        removeFilter(key) {
            const params = this.getParams();

            const keyMap = {
                'type': ['type', 'tipo'],
                'credit': ['credit', 'crediti'],
                'occupation': ['professione', 'occupation', '_occupation_slug'],
                'search': ['ts', 'search'],
                'product_type': ['product_type'],
            };

            const paramKeys = keyMap[key] || [key];
            paramKeys.forEach(k => params.delete(k));

            // Se rimuovo tipo ma c'è professione nel path, preservala
            if (key === 'type') {
                const path = window.location.pathname;
                const comboMatch = path.match(/\/corsi-ecm\/(?:fad|residenziali)\/([^/]+)\/?$/);
                if (comboMatch) {
                    params.set('_occupation_slug', comboMatch[1]);
                }
            }

            // Se rimuovo professione ma c'è tipo nel path, preservalo
            if (key === 'occupation') {
                const path = window.location.pathname;
                const comboMatch = path.match(/\/corsi-ecm\/(fad|residenziali)\//);
                if (comboMatch) {
                    params.set('type', SEGMENT_TYPES[comboMatch[1]]);
                }
            }

            this.navigate(params);
        },

        /**
         * Toggle sidebar mobile (drawer).
         */
        toggleMobile() {
            this.mobileOpen = !this.mobileOpen;
        },
    }));
}
